import * as React from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { session, type SessionUser } from './lib/api';
import { AgenciesScreen, AgencyDetailScreen } from './screens/Agencies';
import { TemplatesScreen } from './screens/Templates';
import { TemplateEditorScreen } from './screens/TemplateEditor';
import { ClientsScreen, ClientDetailScreen } from './screens/Clients';
import { CampaignsScreen, CampaignDetailScreen, AdGroupDetailScreen } from './screens/Campaigns';
import { ReportBuilderScreen, CampaignReportScreen, WindowResolverScreen } from './screens/ReportBuilder';
import { CampaignFormScreen } from './screens/CampaignForm';
import { ClientFormScreen } from './screens/ClientForm';
import { SpreadsheetScreen } from './screens/Spreadsheet';
import { EblastCampaignsScreen } from './screens/Eblast';
import { EblastDeployScreen, EblastReportScreen } from './screens/EblastDeploy';
import { AdGroupFormScreen } from './screens/AdGroupForm';
import { AdFormScreen } from './screens/AdForm';
import { AccountsScreen } from './screens/Accounts';

/**
 * Sign-out is a form post, not a link.
 *
 * A GET that ends a session can be triggered by anything that loads a URL — a prefetch,
 * an image tag, a link in an email. Destructive actions are POSTs.
 */
function SignOut() {
  return (
    <form method="post" action="/auth/logout" className="signout">
      <button type="submit">Sign out</button>
    </form>
  );
}

/**
 * The app bar, and the only place the signed-in user's role is read.
 *
 * Accounts is shown to administrators alone. The link being hidden is NOT the control —
 * the `/v1/admin/users` API behind the screen is admin-only on the server, so a reviewer
 * who opens `/accounts` is refused and shown why. This just stops offering a door that
 * will not open.
 */
/**
 * The "Campaigns" flyout — RTB and EBlast campaign lists under one nav item. Opens on hover or keyboard
 * focus (CSS `:hover` / `:focus-within`); the trigger is highlighted while either child route is active.
 */
function CampaignsFlyout() {
  const { pathname } = useLocation();
  const active = pathname === '/campaigns' || pathname.startsWith('/campaigns/') || pathname.startsWith('/eblast');
  return (
    <div className="navflyout">
      <span className={`navflyout-trigger${active ? ' active' : ''}`} tabIndex={0} role="button" aria-haspopup="true">
        Campaigns <span className="caret">▾</span>
      </span>
      <div className="navflyout-menu">
        <NavLink to="/campaigns">RTB Campaigns</NavLink>
        <NavLink to="/eblast">EBlast Campaigns</NavLink>
      </div>
    </div>
  );
}

/** The two API-doc consoles (public + private/management), server-rendered pages opened in a new tab. */
function DeveloperFlyout() {
  return (
    <div className="navflyout">
      <span className="navflyout-trigger" tabIndex={0} role="button" aria-haspopup="true">
        Developer <span className="caret">▾</span>
      </span>
      <div className="navflyout-menu">
        <a href="/public/v1/docs" target="_blank" rel="noopener noreferrer">Public API</a>
        <a href="/v1/docs" target="_blank" rel="noopener noreferrer">Private API</a>
      </div>
    </div>
  );
}

function Chrome() {
  const [user, setUser] = React.useState<SessionUser | null>(null);

  React.useEffect(() => {
    let live = true;
    // A failure here is not worth surfacing: every screen already redirects to sign-in on
    // its own 401, and a missing nav link is better than an error banner over the app.
    session().then((s) => { if (live) setUser(s.user); }).catch(() => {});
    return () => { live = false; };
  }, []);

  return (
    <header className="chrome">
      <a className="brand" href="/" aria-label="PDMG Reporting — home">
        <img className="brand-logo" src="https://api.directdigitalplatform.com/images/logo.png" alt="PDMG Reporting" />
      </a>
      <nav>
        <CampaignsFlyout />
        <NavLink to="/spreadsheet">Spreadsheet</NavLink>
        <NavLink to="/clients">Clients</NavLink>
        <NavLink to="/agencies">Agencies</NavLink>
        {user?.role === 'admin' && <NavLink to="/templates">Templates</NavLink>}
        <DeveloperFlyout />
        {user?.role === 'admin' && <NavLink to="/accounts">Accounts</NavLink>}
      </nav>
      {user && <span className="who" title={user.email}>{user.name || user.email}</span>}
      <SignOut />
    </header>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Chrome />

      <main>
        <Routes>
          <Route path="/" element={<Navigate to="/campaigns" replace />} />
          <Route path="/campaigns" element={<CampaignsScreen />} />
          <Route path="/eblast" element={<EblastCampaignsScreen />} />
          <Route path="/eblast/deploy/:id" element={<EblastDeployScreen />} />
          <Route path="/eblast/report/:so" element={<EblastReportScreen />} />
          <Route path="/spreadsheet" element={<SpreadsheetScreen />} />
          <Route path="/campaigns/new" element={<CampaignFormScreen />} />
          <Route path="/campaigns/:id/edit" element={<CampaignFormScreen />} />
          <Route path="/campaigns/:campaignId/adgroups/new" element={<AdGroupFormScreen />} />
          <Route path="/adgroups/:id/edit" element={<AdGroupFormScreen />} />
          <Route path="/adgroups/:adgroupId/ads/new" element={<AdFormScreen />} />
          <Route path="/ads/:id/edit" element={<AdFormScreen />} />
          <Route path="/campaigns/:id" element={<CampaignDetailScreen />} />
          {/* Resolves the campaign's report and forwards to /reports/:reportId. */}
          <Route path="/campaigns/:id/report" element={<CampaignReportScreen />} />
          <Route path="/adgroups/:id" element={<AdGroupDetailScreen />} />
          <Route path="/reports/:reportId" element={<ReportBuilderScreen />} />
          {/* A static, shareable URL keyed on SO + window: generates on demand and forwards to the report. */}
          <Route path="/reports/so/:reportSo/:start/:end" element={<WindowResolverScreen />} />
          {/* SO only: resolve the campaign's flight window (ad-group min start → max end) and open it. */}
          <Route path="/reports/so/:reportSo" element={<WindowResolverScreen />} />
          <Route path="/clients" element={<ClientsScreen />} />
          <Route path="/clients/:id/edit" element={<ClientFormScreen />} />
          <Route path="/clients/:id" element={<ClientDetailScreen />} />
          <Route path="/agencies" element={<AgenciesScreen />} />
          <Route path="/templates" element={<TemplatesScreen />} />
          <Route path="/templates/:id/:version" element={<TemplateEditorScreen />} />
          <Route path="/agencies/:id" element={<AgencyDetailScreen />} />
          {/* Accounts — admin-only; the API refuses a reviewer and the screen explains why. */}
          <Route path="/accounts" element={<AccountsScreen />} />
          <Route path="*" element={<div className="banner">Not found.</div>} />
        </Routes>
      </main>
    </BrowserRouter>
  );
}
